import {
  Button,
  Col,
  Container,
  Row,
  Typography,
  getTypographyDefaultColor,
  useMediaQuery
} from '@ag1/ui';
import { toPlainText } from '@portabletext/react';
import { buttonClickEvent, clickPurchaseIntentEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import Head from 'next/head';
import { usePageContext } from 'processes/Page/PageContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale } from 'shared/hooks/useLocale';
import { UnifiedButtonType } from 'shared/studio/UnifiedButton/UnifiedButton.types';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { useStoreValue } from 'state/hooks/useStore';
import { HeroAnimated as HeroAnimatedProps, SanityKeyed } from 'types/studio/studio';
import { getSanityLinkUrl } from '../../utils/getSanityLinkUrl';
import CustomPortableText from '../PortableText/PortableText';
import StudioResponsiveMedia from '../StudioResponsiveMedia/StudioResponsiveMedia';
import { CommonSectionProps } from '../types';
import { UnifiedButton } from '../UnifiedButton';
import styles from './HeroAnimated.module.scss';
import { heroSchema } from './heroSchema';
import Reviews from './Reviews/Reviews';

const HeroAnimated = (props: SanityKeyed<HeroAnimatedProps> & CommonSectionProps) => {
  const {
    _key: key,
    _type: sectionType,
    headline,
    headlineAnimated,
    subtitle,
    unifiedButton,
    additionalLinkSwitch,
    additionalLink,
    additionalTarget,
    heroMedia,
    layoutType,
    hugPadding,
    ratingValue,
    ratingTitle,
    reviews,
    theme,
    visibleOnPageLoad
  } = props;

  const store = useStoreValue();
  const { shouldDisableUnifiedButton, shouldDisableCTALink, maybeReplaceOfferlessCheckoutLink } =
    usePageContext();

  const [activeIndex, setActiveIndex] = useState(0);
  const atSmall = useMediaQuery('s');
  const locale = useLocale();

  const time = atSmall ? 3200 : 2500;

  const additionalLinkUrl = useMemo(
    () => maybeReplaceOfferlessCheckoutLink(getSanityLinkUrl(additionalLink?.btnLink, locale)),
    [maybeReplaceOfferlessCheckoutLink, additionalLink?.btnLink, locale]
  );

  const handleAdditionalLinkButtonClick = () => {
    buttonClickEvent({
      clickText: additionalLink?.portableText ? toPlainText(additionalLink?.portableText) : '',
      url: additionalLinkUrl,
      location: sectionType
    });
  };

  useEffect(() => {
    if (headlineAnimated && headlineAnimated.length > 1) {
      const interval = setInterval(() => {
        if (headlineAnimated) {
          setActiveIndex((prevIndex) => (prevIndex + 1) % headlineAnimated.length);
        }
      }, time);

      return () => clearInterval(interval);
    }

    return setActiveIndex(0);
  }, [headlineAnimated, time]);

  const disableUnifiedButton = useMemo(
    () => shouldDisableUnifiedButton(unifiedButton),
    [shouldDisableUnifiedButton, unifiedButton]
  );

  const handleCTAButtonClick = useCallback(() => {
    clickPurchaseIntentEvent({
      store,
      clickText: unifiedButton?.baseButtonLinkV2?.portableText
        ? toPlainText(unifiedButton?.baseButtonLinkV2?.portableText)
        : undefined,
      location: sectionType,
      url: unifiedButton?.baseButtonLinkV2?.btnLink?.internal?.slug?.current
    });
  }, [sectionType, store, unifiedButton]);

  return (
    <section className={styles.root} id={key}>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: heroSchema
          }}
          key="organization-jsonld"
        />
      </Head>
      <div className={clsx(styles.container, styles[`container-layout-${layoutType}`])}>
        <StudioResponsiveMedia
          {...heroMedia}
          className={styles.media}
          eagerLoading={visibleOnPageLoad}
        />
        <Container narrow>
          <Row>
            <Col
              span={{ xxs: 12, s: 9, m: 8, xm: 5, l: 5 }}
              className={clsx(styles.content, styles[`content-layout-${layoutType}`], {
                [styles[`content-padding-${hugPadding}`]]: layoutType === 'hug'
              })}>
              <div className={styles.wrap}>
                <Typography
                  component="div"
                  variant="headline"
                  size="2"
                  color={getTypographyDefaultColor(theme)}
                  noPadding
                  className={styles.headline}>
                  <CustomPortableText value={headline} />
                </Typography>
                {headlineAnimated && headlineAnimated.length > 0 && (
                  <div className={styles.headlineAnimatedWrapper}>
                    {headlineAnimated.map((item, i) => (
                      <Typography
                        key={item._key}
                        component="div"
                        variant="headline"
                        size="1"
                        color={getTypographyDefaultColor(theme)}
                        noPadding
                        className={clsx(
                          styles.headlineAnimated,
                          headlineAnimated.length === 1 && styles.onlyChild,
                          i === activeIndex && styles.active,
                          (i === activeIndex + 1 ||
                            (activeIndex === headlineAnimated.length - 1 && i === 0)) &&
                            styles.next
                        )}>
                        <CustomPortableText value={item.content} />
                      </Typography>
                    ))}
                  </div>
                )}
                {subtitle && (
                  <Typography
                    component="div"
                    variant="portalText"
                    size="1"
                    noPadding
                    color={getTypographyDefaultColor(theme)}
                    className={styles.subtitle}>
                    <CustomPortableText value={subtitle} />
                  </Typography>
                )}
              </div>
              {unifiedButton?.switchButton !== 'noButton' && (
                <div className={styles.buttonWrapper}>
                  <UnifiedButton
                    unifiedButton={unifiedButton as UnifiedButtonType}
                    onClick={handleCTAButtonClick}
                    sectionType={sectionType}
                    className={clsx(styles.button, getThemeCSSClass(theme))}
                    size={{ xxs: 'medium', l: 'large' }}
                    theme={theme}
                    isDisabled={disableUnifiedButton}
                  />
                  {additionalLinkSwitch && additionalLink && additionalLinkUrl && (
                    <Button
                      className={clsx(styles.altButton)}
                      size={{ l: 'large', xxs: 'medium' }}
                      href={additionalLinkUrl}
                      disabled={shouldDisableCTALink(additionalLinkUrl)}
                      variant={additionalLink.variant}
                      rounded
                      icon={additionalLink.showIcon && additionalLink.iconOptions?.icon}
                      iconPosition={additionalLink.iconOptions?.position}
                      data-gtm-skip-global
                      onClick={handleAdditionalLinkButtonClick}
                      target={additionalTarget ? '_blank' : '_self'}
                      theme={theme}>
                      {additionalLink.portableText && (
                        <CustomPortableText value={additionalLink.portableText} />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <Reviews
        ratingValue={ratingValue}
        ratingTitle={ratingTitle}
        reviews={reviews}
        theme={theme}
      />
    </section>
  );
};

export default HeroAnimated;
